/**
 * The admin API surface, as this app consumes it.
 *
 * Every function here delegates to `ui/src/api/generated.ts` — nothing in this
 * app hand-writes a fetch, and as of this file's last revision nothing casts
 * past the generated types either. The eight `as unknown as` casts that used to
 * live here existed because the route registry declared no `Response:` for
 * tenants.list, tenants.provisioning, tenants.migrate, tenants.usage,
 * plans.list, admins.list, tenantUsers.list and tenantAudit.list, and the two
 * `client.request()` calls existed because the same registry modelled no query
 * parameters for the users and audit routes. All ten now do, so the
 * accommodations are gone: a cast is what turns a server-side field rename into
 * a permanently blank column instead of a build failure.
 *
 * The wrappers remain — they give screens a call that takes a tenant id rather
 * than a `{ id }` params object, and they are the one place `client` versus
 * `bareClient` is decided.
 */

import {
  adminAdminsCreate,
  adminAdminsDelete,
  adminAdminsList,
  adminAdminsUpdate,
  adminAuthAcceptInvite,
  adminAuthForgotPassword,
  adminAuthLogin,
  adminAuthLogout,
  adminAuthMe,
  adminAuthMfaConfirm,
  adminAuthMfaEnroll,
  adminAuthRefresh,
  adminAuthResetPassword,
  adminDashboardGet,
  adminPlansCreate,
  adminPlansGet,
  adminPlansList,
  adminPlansUpdate,
  adminSupportSessionsCreate,
  adminSupportSessionsRevoke,
  adminTenantAuditList,
  adminTenantUsersCreate,
  adminTenantUsersDeactivate,
  adminTenantUsersList,
  adminTenantUsersResendInvite,
  adminTenantUsersResetMfa,
  adminTenantUsersResetPassword,
  adminTenantUsersRevokeSessions,
  adminTenantUsersSetPassword,
  adminTenantUsersUnlock,
  adminTenantsCreate,
  adminTenantsGet,
  adminTenantsList,
  adminTenantsMigrate,
  adminTenantsProvisioning,
  adminTenantsReactivate,
  adminTenantsResume,
  adminTenantsSuspend,
  adminTenantsUpdate,
  adminTenantsUsage,
  type AckResponse,
  type AdminCreateRequest,
  type AdminLoginRequest,
  type AdminMFAConfirmResponse,
  type AdminMFAEnrollResponse,
  type AdminUpdateRequest,
  type AuthTokenResponse,
  type CreateTenantRequest,
  type CreateTenantUserRequest,
  type CreateTenantUserResponse,
  type DashboardResponse,
  type PatchTenantRequest,
  type Plan,
  type PlanCreateRequest,
  type PlanUpdateRequest,
  type SetTenantUserPasswordRequest,
  type SetTenantUserPasswordResponse,
  type SupportSessionResponse,
  type Tenant,
  type TenantDetail,
  type TenantListQuery,
  type TenantMigrateResponse,
  type User,
} from '@erpx/ui/api/generated'

import { bareClient, client } from './client'
import type {
  AuditLogEntry,
  AuditQuery,
  Page,
  ProvisioningStep,
  TenantSummary,
  TenantUserSummary,
  UsageDaily,
} from './types'

/* ------------------------------------------------------------------- auth */

export function login(body: AdminLoginRequest): Promise<AuthTokenResponse> {
  return adminAuthLogin(bareClient, body)
}

export function refresh(refreshToken: string): Promise<AuthTokenResponse> {
  return adminAuthRefresh(bareClient, { refreshToken })
}

export function me(): Promise<User> {
  return adminAuthMe(client)
}

export function logout(): Promise<void> {
  return adminAuthLogout(client)
}

export function mfaEnroll(): Promise<AdminMFAEnrollResponse> {
  return adminAuthMfaEnroll(client)
}

export function mfaConfirm(code: string): Promise<AdminMFAConfirmResponse> {
  return adminAuthMfaConfirm(client, { code })
}

/**
 * Finish an invited operator's account by setting their password.
 *
 * `bareClient`, like login and refresh: these three routes are `Public: true`
 * and are reached by someone who has no session at all. The response is an
 * acknowledgement, not a token pair — an operator who has just set a password
 * still has to sign in, and in this realm still has to enrol an authenticator
 * before the console opens (§6). That is deliberate: the invite link arrives by
 * email, and a link that both proves identity and grants a session would make
 * a forwarded email a working account.
 */
export function acceptInvite(token: string, password: string): Promise<AckResponse> {
  return adminAuthAcceptInvite(bareClient, { token, password })
}

/**
 * Ask for a reset link. 204 whether or not the address exists — the server
 * answers identically either way (internal/adminapi/auth.go forgotPassword), so
 * this endpoint cannot be used to enumerate platform operators.
 */
export function forgotPassword(email: string): Promise<void> {
  return adminAuthForgotPassword(bareClient, { email })
}

export function resetPassword(token: string, password: string): Promise<AckResponse> {
  return adminAuthResetPassword(bareClient, { token, password })
}

/* ---------------------------------------------------------------- tenants */

export function listTenants(query: TenantListQuery): Promise<Page<TenantSummary>> {
  return adminTenantsList(client, query)
}

export function createTenant(
  body: CreateTenantRequest,
  idempotencyKey?: string,
): Promise<Tenant> {
  return adminTenantsCreate(client, body, idempotencyKey)
}

export function getTenant(id: string): Promise<TenantDetail> {
  return adminTenantsGet(client, { id })
}

export function getProvisioningSteps(id: string): Promise<ProvisioningStep[]> {
  return adminTenantsProvisioning(client, { id })
}

export function resumeProvisioning(id: string): Promise<Tenant> {
  return adminTenantsResume(client, { id })
}

export function suspendTenant(id: string, reason: string): Promise<Tenant> {
  return adminTenantsSuspend(client, { id }, { reason })
}

export function reactivateTenant(id: string): Promise<Tenant> {
  return adminTenantsReactivate(client, { id })
}

export function updateTenant(id: string, body: PatchTenantRequest): Promise<Tenant> {
  return adminTenantsUpdate(client, { id }, body)
}

export function migrateTenant(id: string): Promise<TenantMigrateResponse> {
  return adminTenantsMigrate(client, { id })
}

export function getTenantUsage(id: string, days: number): Promise<UsageDaily[]> {
  return adminTenantsUsage(client, { id }, { days })
}

/* ------------------------------------------------------------------ plans */

export function listPlans(): Promise<Plan[]> {
  return adminPlansList(client)
}

export function getPlan(id: string): Promise<Plan> {
  return adminPlansGet(client, { id })
}

export function createPlan(body: PlanCreateRequest): Promise<Plan> {
  return adminPlansCreate(client, body)
}

export function updatePlan(id: string, body: PlanUpdateRequest): Promise<Plan> {
  return adminPlansUpdate(client, { id }, body)
}

/* ---------------------------------------------------------------- support */

export function createSupportSession(
  tenantId: string,
  reason: string,
): Promise<SupportSessionResponse> {
  return adminSupportSessionsCreate(client, { id: tenantId }, { reason })
}

export function revokeSupportSession(sessionId: string): Promise<void> {
  return adminSupportSessionsRevoke(client, { id: sessionId })
}

export function listTenantUsers(
  tenantId: string,
  query: { cursor?: string; limit?: number } = {},
): Promise<Page<TenantUserSummary>> {
  return adminTenantUsersList(client, { id: tenantId }, query)
}

/**
 * Create a user inside a tenant's schema on the operator's behalf.
 *
 * `credentialMode` decides what the operator walks away with: `invite` mails a
 * link and returns nothing, `set` takes the password from the body, `generate`
 * mints one and returns it in `generatedPassword` — **once**, from a value the
 * server stores nowhere. The idempotency key is not optional in practice: the
 * route is declared `Idempotent: true` and a double-submitted create would
 * otherwise be a second account, or a 409 on the email unique index.
 */
export function createTenantUser(
  tenantId: string,
  body: CreateTenantUserRequest,
  idempotencyKey?: string,
): Promise<CreateTenantUserResponse> {
  return adminTenantUsersCreate(client, { id: tenantId }, body, idempotencyKey)
}

/**
 * Write a password directly onto a tenant user.
 *
 * Stands beside `resetTenantUserPassword` rather than replacing it — mailing a
 * link is still the only path that leaves nobody but the user knowing the
 * password, and it remains the better one whenever the user has a working
 * mailbox. This is for the cases it cannot serve: no mailer configured, an
 * air-gapped self-host install, an operator sitting beside the person.
 *
 * Two consequences the calling screen has to state: every live session for that
 * user is revoked, and any unredeemed invite or reset token is cleared
 * (`identity.SetTenantUserPassword`), so a link already in their inbox stops
 * working.
 */
export function setTenantUserPassword(
  tenantId: string,
  userId: number,
  body: SetTenantUserPasswordRequest,
): Promise<SetTenantUserPasswordResponse> {
  return adminTenantUsersSetPassword(client, { id: tenantId, userId: String(userId) }, body)
}

export function resetTenantUserPassword(
  tenantId: string,
  userId: number,
): Promise<AckResponse> {
  return adminTenantUsersResetPassword(client, { id: tenantId, userId: String(userId) })
}

export function unlockTenantUser(tenantId: string, userId: number): Promise<AckResponse> {
  return adminTenantUsersUnlock(client, { id: tenantId, userId: String(userId) })
}

export function resetTenantUserMfa(tenantId: string, userId: number): Promise<AckResponse> {
  return adminTenantUsersResetMfa(client, { id: tenantId, userId: String(userId) })
}

export function resendTenantUserInvite(tenantId: string, userId: number): Promise<AckResponse> {
  return adminTenantUsersResendInvite(client, { id: tenantId, userId: String(userId) })
}

export function deactivateTenantUser(tenantId: string, userId: number): Promise<AckResponse> {
  return adminTenantUsersDeactivate(client, { id: tenantId, userId: String(userId) })
}

export function revokeTenantUserSessions(
  tenantId: string,
  userId: number,
): Promise<AckResponse> {
  return adminTenantUsersRevokeSessions(client, { id: tenantId, userId: String(userId) })
}

/**
 * The tenant's audit log. The route whitelists entity, action, actorId and
 * docNo alongside cursor and limit (internal/adminapi/support.go tenantAudit),
 * and `AuditQuery` is the generated declaration of exactly that set.
 */
export function listTenantAudit(
  tenantId: string,
  query: AuditQuery = {},
): Promise<Page<AuditLogEntry>> {
  return adminTenantAuditList(client, { id: tenantId }, query)
}

/* ----------------------------------------------------- platform admins */

export function listAdmins(): Promise<User[]> {
  return adminAdminsList(client)
}

export function inviteAdmin(body: AdminCreateRequest): Promise<User> {
  return adminAdminsCreate(client, body)
}

export function updateAdmin(id: string, body: AdminUpdateRequest): Promise<User> {
  return adminAdminsUpdate(client, { id }, body)
}

export function removeAdmin(id: string): Promise<void> {
  return adminAdminsDelete(client, { id })
}

/* -------------------------------------------------------------- dashboard */

export function getDashboard(): Promise<DashboardResponse> {
  return adminDashboardGet(client)
}

export type {
  AckResponse,
  AdminCreateRequest,
  AdminLoginRequest,
  AdminMFAConfirmResponse,
  AdminMFAEnrollResponse,
  AdminUpdateRequest,
  AuthTokenResponse,
  CreateTenantRequest,
  CreateTenantUserRequest,
  CreateTenantUserResponse,
  DashboardResponse,
  PatchTenantRequest,
  Plan,
  PlanCreateRequest,
  PlanUpdateRequest,
  SetTenantUserPasswordRequest,
  SetTenantUserPasswordResponse,
  SupportSessionResponse,
  Tenant,
  TenantDetail,
  TenantListQuery,
  TenantMigrateResponse,
  User,
}
